import React from 'react';
import { styled } from '@compiled/react';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout.tsx';
import {
	BUSINESS_NAV_HEIGHT,
	NAV4_BUSINESS_NAV_HEIGHT,
	Tokens,
} from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH,
	UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH,
	UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH,
	UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH,
} from '@atlassian/jira-navigation-system/src/index.tsx';
import { LEGACY_PAGE_INSET_BLOCK_START } from '@atlassian/jira-platform-ui-banners-jsm-packaging-updates/src/constants.tsx';
import { useBannerJsmPackagingUpdates } from '@atlassian/jira-platform-ui-banners-jsm-packaging-updates/src/controllers/index.tsx';
import { useProjectTheme } from '@atlassian/jira-project-theme-page-container/src/controllers/project-theme/index.tsx';
import { isSystemNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { isIssuesNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/issues/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LayoutMainWrapper = styled.div({
	display: 'contents',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > :first-of-type': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `calc( 100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH} ) !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP})`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& #jira-frontend': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `calc( 100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH} ) !important`,
	},
});

/* The main goal of the LegacyPageRightSidebarStyles is to make:
 *	- the legacy-pages (<id="content"/>) width the same as the layout-manager content area
 *	- the legacy-pages (<id="content"/>) left postion relative to the left-side-panel and left-sidebar of the layout-manager
 *
 * Q) Why do we need to use a style tag?
 *
 * A) The legacy-pages and the react-APP are at the same level in the DOM tree and they are not directly related (that means the
 * legacy-page is not a child of the react-app)
 */
export const LegacyPageRightSidebarStyles = () => {
	const { hasProjectViewNavigation } = useProjectTheme();
	const { isEnabled: isJsmPackagingUpdatesEnabled } = useBannerJsmPackagingUpdates();
	const currentRoute = useRouterRoute();
	const isJiraIssueTermBannerEnabled =
		isSystemNavPath(currentRoute.path) || isIssuesNavPath(currentRoute.path);

	const commonContentStyles = `
            border-top: 0 !important;
            position: absolute;
            left: calc(${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} + ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH});
            z-index: auto;
        `;

	let contentStyles;

	// Here be dragons! The contentStyles uses bottom and right (over width/height) to remove spuriously added horizontal scrollbars.
	// For more details see go/j/MAGMA-1313 and https://jira.atlassian.com/browse/JRACLOUD-81597
	contentStyles = `
            top: calc(${PAGE_LAYOUT_OFFSET_TOP} + 2px) !important;
            right: calc(${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} + ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH});
            bottom: 0;
            padding: 0 40px;
            margin: 0;
        `;

	if (isJsmPackagingUpdatesEnabled) {
		contentStyles = `
            top: calc(${PAGE_LAYOUT_OFFSET_TOP} + ${LEGACY_PAGE_INSET_BLOCK_START}) !important;
            height: calc(100% - ${PAGE_LAYOUT_OFFSET_TOP});
            width: calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH}) !important;
            padding: 0 40px;
            margin: 0;
		`;
	}

	if (isJiraIssueTermBannerEnabled) {
		contentStyles = `
            top: calc(${PAGE_LAYOUT_OFFSET_TOP} + ${LEGACY_PAGE_INSET_BLOCK_START}) !important;
            height: calc(100% - ${PAGE_LAYOUT_OFFSET_TOP});
            width: calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH}) !important;
            padding: 0 40px;
            margin: 0;
		`;
	}

	if (hasProjectViewNavigation) {
		contentStyles = `
            top: calc(${PAGE_LAYOUT_OFFSET_TOP} + ${getWillShowNav4() ? NAV4_BUSINESS_NAV_HEIGHT : BUSINESS_NAV_HEIGHT} + 2px) !important;
            height: ${Tokens.WRAPPER_HEIGHT};
            margin: ${Tokens.WRAPPER_PADDING};
            border-radius: ${Tokens.WRAPPER_BORDER_RADIUS};
            background-color: ${Tokens.WRAPPER_BACKGROUND};
            overflow: auto;
            padding: 0 40px;
        `;
	}

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
		<style
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{
				__html: `
                #navigation-app {
                    width: 100%;
                    z-index: auto;
                    order: initial;
                }
                #page-body > #content {
                    ${contentStyles}
                    ${commonContentStyles}
                }
                /* start GIN/NIN/oldIssueView */
                .page-issue-navigator #page-body > #content,
                .ajax-issue-search-and-view  #page-body > div#content {
                    padding-right: 0;
                    height: calc(100% - ${PAGE_LAYOUT_OFFSET_TOP}) !important;
                }
                /* end GIN/NIN/oldIssueView */
                #layout-main {
                    width: calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH}) !important;
                }
            `,
			}}
		/>
	);
};

const useRouterRoute = createRouterSelector((state) => state.route);

export const LegacyEntryStyleOverrides = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style
		// eslint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `
                    #page-body > #content {
                        margin: 0;
                        border-top: none;
                    }
                    /* make it zindex 1 for easier to click, current experience is not good */
                    .page-type-admin.page-type-admin-breadcrumbs .generalAdminHeading.aui-page-header {
                        z-index: 1;
                    }
                    /* end /secure/project/ViewProjects.jspa */
                `,
		}}
	/>
);
